<template>
  <Teleport to="#scrollbox">
    <div v-if="!isCompleted && (unfilledIndices.length > 0 || isAllRequiredDone)" class="ftab-anchor" :style="{ left: anchorLeft + 'px' }">
      <Transition name="ftab">
        <button
          v-if="aboveTabs.length && unfilledIndices.length > 0"
          class="ftab-btn ftab-overflow"
          :style="{ position: 'fixed', top: '78px', left: anchorLeft + 'px', zIndex: 50 }"
          @click="$emit('jump', aboveTabs[0].stepIndex)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="M5 12l7-7 7 7"/></svg>
          <span>{{ aboveTabs.length }} above</span>
        </button>
      </Transition>

      <TransitionGroup name="ftab">
        <button
          v-for="tab in spacedTabs"
          :key="tab.stepIndex"
          class="ftab-btn"
          :class="tab.stepIndex === currentStep ? 'ftab-active' : 'ftab-idle'"
          :style="{ position: 'absolute', top: tab.docY + 'px', zIndex: 30 }"
          @click="$emit('jump', tab.stepIndex)"
        >
          <component
            :is="typeIcon(tab.field.type)"
            :width="13"
            :height="13"
            :stroke-width="tab.stepIndex === currentStep ? 2.5 : 2"
          />
          <span class="ftab-label">{{ fieldLabel(tab.field) }}</span>
          <span v-if="tab.stepIndex === currentStep" class="ftab-pulse" />
        </button>
      </TransitionGroup>

      <Transition name="ftab">
        <button
          v-if="belowTabs.length && !isAllRequiredDone"
          class="ftab-btn ftab-overflow"
          :style="{ position: 'fixed', bottom: formClearance + 'px', left: anchorLeft + 'px', zIndex: 50 }"
          @click="$emit('jump', belowTabs[0].stepIndex)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="M19 12l-7 7-7-7"/></svg>
          <span>{{ belowTabs.length }} below</span>
        </button>
      </Transition>

      <Transition name="ftab">
        <button
          v-if="isAllRequiredDone"
          class="ftab-btn ftab-done"
          :style="doneTabStyle"
          @click="$emit('complete')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          <span>Done</span>
        </button>
      </Transition>
    </div>
  </Teleport>
</template>

<script>
import {
  IconWritingSign, IconCalendarEvent, IconLetterCaseUpper, IconTextSize,
  IconPhoneCheck, IconCheckbox, IconSelect, IconSquareNumber1, IconPhoto,
  IconPaperclip, IconCreditCard, IconColumns3, IconChecks, IconCircleDot
} from '@tabler/icons-vue'

const FIELD_TYPE_LABELS = {
  signature: 'Signature',
  initials: 'Initials',
  date: 'Date',
  text: 'Text',
  phone: 'Phone',
  checkbox: 'Checkbox',
  select: 'Select',
  number: 'Number',
  image: 'Image',
  file: 'File',
  payment: 'Payment',
  cells: 'Cells',
  multiple: 'Multiple'
}

const FIELD_TYPE_ICONS = {
  signature: 'IconWritingSign',
  initials: 'IconLetterCaseUpper',
  date: 'IconCalendarEvent',
  text: 'IconTextSize',
  phone: 'IconPhoneCheck',
  checkbox: 'IconCheckbox',
  select: 'IconSelect',
  number: 'IconSquareNumber1',
  image: 'IconPhoto',
  file: 'IconPaperclip',
  payment: 'IconCreditCard',
  cells: 'IconColumns3',
  multiple: 'IconChecks'
}

const MIN_TAB_GAP = 42

function getPosRelativeTo (el, ancestor) {
  const elRect = el.getBoundingClientRect()
  const anRect = ancestor.getBoundingClientRect()
  return {
    top: elRect.top - anRect.top + ancestor.scrollTop,
    left: elRect.left - anRect.left + (ancestor.scrollLeft || 0)
  }
}

export default {
  name: 'FieldTabs',
  components: {
    IconWritingSign, IconCalendarEvent, IconLetterCaseUpper, IconTextSize,
    IconPhoneCheck, IconCheckbox, IconSelect, IconSquareNumber1, IconPhoto,
    IconPaperclip, IconCreditCard, IconColumns3, IconChecks, IconCircleDot
  },
  props: {
    stepFields: { type: Array, required: true },
    values: { type: Object, required: true },
    currentStep: { type: Number, required: true },
    isCompleted: { type: Boolean, default: false },
    scrollEl: { default: null },
    formClearance: { type: Number, default: 175 }
  },
  emits: ['jump', 'complete'],
  data () {
    return {
      rawPositions: [],
      docLeft: 0,
      lastKnownDocLeft: 0,
      lastCompletedFieldY: null,
      scrollTop: 0,
      viewportHeight: window.innerHeight,
      firstPageTop: 0
    }
  },
  computed: {
    anchorLeft () {
      return this.lastKnownDocLeft || this.docLeft
    },
    doneTabStyle () {
      if (this.lastCompletedFieldY != null) {
        return { position: 'absolute', top: this.lastCompletedFieldY + 'px', zIndex: 50 }
      }
      return { position: 'fixed', bottom: this.formClearance + 'px', left: this.anchorLeft + 'px', zIndex: 50 }
    },
    anyRequiredFields () {
      return this.stepFields.some(fields => fields.some(f => f.required))
    },
    requiredUnfilledIndices () {
      return this.stepFields.reduce((acc, fields, index) => {
        if (fields.some(f => f.required && this.isEmpty(this.values[f.uuid]))) {
          acc.push(index)
        }
        return acc
      }, [])
    },
    unfilledIndices () {
      return this.stepFields.reduce((acc, fields, index) => {
        if (fields.some(f => this.isEmpty(this.values[f.uuid]))) {
          acc.push(index)
        }
        return acc
      }, [])
    },
    isAllRequiredDone () {
      return this.anyRequiredFields && this.requiredUnfilledIndices.length === 0
    },
    allTabs () {
      return this.unfilledIndices.map(stepIndex => {
        const field = this.stepFields[stepIndex]?.[0]
        const pos = this.rawPositions.find(p => p.stepIndex === stepIndex)
        return field && pos != null ? { stepIndex, field, docY: pos.docY } : null
      }).filter(Boolean)
    },
    aboveTabs () {
      return this.allTabs.filter(t => t.docY < this.scrollTop + 88)
    },
    belowTabs () {
      return this.allTabs.filter(t => t.docY > this.scrollTop + this.viewportHeight - 150)
    },
    spacedTabs () {
      const visible = this.allTabs
        .filter(t => t.docY >= this.scrollTop + 88 && t.docY <= this.scrollTop + this.viewportHeight - 150)
        .sort((a, b) => a.docY - b.docY)
      const spaced = []
      visible.forEach(tab => {
        const prev = spaced[spaced.length - 1]
        const y = prev ? Math.max(tab.docY, prev.docY + MIN_TAB_GAP) : tab.docY
        spaced.push({ ...tab, docY: y })
      })
      return spaced
    }
  },
  watch: {
    values: {
      handler () { this.$nextTick(() => this.updatePositions()) },
      deep: true
    },
    currentStep () { this.$nextTick(() => this.updatePositions()) },
    stepFields () { this.$nextTick(() => this.updatePositions()) },
    isAllRequiredDone (newVal) {
      if (newVal) {
        const pos = this.rawPositions.find(p => p.stepIndex === this.currentStep)
        if (pos) {
          this.lastCompletedFieldY = pos.docY
        } else if (this.rawPositions.length > 0) {
          this.lastCompletedFieldY = this.rawPositions[this.rawPositions.length - 1].docY
        }
      }
    }
  },
  mounted () {
    const scrollbox = document.getElementById('scrollbox')
    if (scrollbox) {
      if (getComputedStyle(scrollbox).position === 'static') {
        scrollbox.style.position = 'relative'
      }
      this._scrollbox = scrollbox
    }
    this._scrollHandler = () => {
      const sbTop = scrollbox ? scrollbox.scrollTop : 0
      this.scrollTop = sbTop > 0 ? sbTop : window.scrollY
    }
    if (scrollbox) {
      scrollbox.addEventListener('scroll', this._scrollHandler, { passive: true })
    }
    window.addEventListener('scroll', this._scrollHandler, { passive: true })
    this._scrollHandler()
    this._resizeHandler = () => {
      this.viewportHeight = window.innerHeight
      this.updatePositions()
    }
    window.addEventListener('resize', this._resizeHandler, { passive: true })
    this.$nextTick(() => this.updatePositions())
    this._t1 = setTimeout(() => this.updatePositions(), 600)
    this._t2 = setTimeout(() => this.updatePositions(), 1500)
  },
  beforeUnmount () {
    if (this._scrollbox) {
      this._scrollbox.removeEventListener('scroll', this._scrollHandler)
    }
    window.removeEventListener('scroll', this._scrollHandler)
    window.removeEventListener('resize', this._resizeHandler)
    clearTimeout(this._t1)
    clearTimeout(this._t2)
  },
  methods: {
    isEmpty (val) {
      if (val == null) return true
      if (val === false) return true
      if (Array.isArray(val)) return val.length === 0
      if (typeof val === 'string') return val.trim().length === 0
      if (typeof val === 'object') return Object.keys(val).length === 0
      return false
    },
    updatePositions () {
      const scrollbox = this._scrollbox || document.getElementById('scrollbox')
      const root = this.$root.$el?.parentNode?.getRootNode() || document
      if (!scrollbox) return
      let docLeftCaptured = false
      let minPageTop = null
      this.rawPositions = this.unfilledIndices.map(stepIndex => {
        const field = this.stepFields[stepIndex]?.[0]
        const area = field?.areas?.[0]
        if (!area) return null
        const pageEl = root.getElementById?.(`page-${area.attachment_uuid}-${area.page}`) ||
                       document.getElementById(`page-${area.attachment_uuid}-${area.page}`)
        if (!pageEl) return null
        const pos = getPosRelativeTo(pageEl, scrollbox)
        if (minPageTop === null || pos.top < minPageTop) minPageTop = pos.top
        const fieldMid = pos.top + area.y * pageEl.offsetHeight + (area.h * pageEl.offsetHeight) / 2 - 16
        const docY = Math.max(pos.top, fieldMid)
        if (!Number.isFinite(docY)) return null
        if (!docLeftCaptured) {
          this.docLeft = pos.left
          if (pos.left > 0) this.lastKnownDocLeft = pos.left
          docLeftCaptured = true
        }
        return { stepIndex, docY }
      }).filter(Boolean)
      if (minPageTop !== null) this.firstPageTop = minPageTop
      if (!docLeftCaptured) {
        const anyPage = root.querySelector?.('[id^="page-"]') || document.querySelector('[id^="page-"]')
        if (anyPage) {
          const p = getPosRelativeTo(anyPage, scrollbox)
          this.docLeft = p.left
          if (p.left > 0) this.lastKnownDocLeft = p.left
          if (minPageTop === null) this.firstPageTop = p.top
        }
      }
    },
    typeIcon (type) {
      return FIELD_TYPE_ICONS[type] || 'IconCircleDot'
    },
    fieldLabel (field) {
      if (field.name && field.name.length > 0 && field.name.length <= 16) return field.name
      return FIELD_TYPE_LABELS[field.type] || 'Field'
    }
  }
}
</script>

<style scoped>
.ftab-anchor {
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
}

.ftab-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 11px 9px 13px;
  font-size: 12.5px;
  font-weight: 600;
  border-radius: 9px 0 0 9px;
  box-shadow: -2px 2px 10px rgba(0,0,0,0.14);
  cursor: pointer;
  user-select: none;
  transform: translateX(-100%);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  white-space: nowrap;
  pointer-events: auto;
  border: none;
  outline: none;
}

.ftab-btn:hover {
  transform: translateX(calc(-100% - 3px));
  box-shadow: -3px 3px 14px rgba(0,0,0,0.20);
}

.ftab-active {
  background: #f59e0b;
  color: #fff;
}

.ftab-idle {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fcd34d !important;
  border-right: none !important;
}

.ftab-overflow {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d !important;
  border-right: none !important;
}

.ftab-done {
  background: #22c55e;
  color: #fff;
  font-size: 12px;
  animation: ftab-pulse-green 2s ease-in-out infinite;
}

.ftab-label {
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ftab-pulse {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgba(255,255,255,0.9);
  animation: ftab-pulse-dot 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes ftab-pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.7); }
}

@keyframes ftab-pulse-green {
  0%, 100% { box-shadow: -2px 2px 8px rgba(34,197,94,0.25); }
  50% { box-shadow: -4px 4px 16px rgba(34,197,94,0.5); }
}

.ftab-enter-active {
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease;
}
.ftab-leave-active {
  transition: transform 0.15s ease-in, opacity 0.12s ease;
}
.ftab-enter-from,
.ftab-leave-to {
  transform: translateX(0%);
  opacity: 0;
}
</style>
